import React, { useState, useEffect } from 'react';
import type { TeamMember, UpdateUserRequest } from '../../types/team';

interface SimpleAccountAccess {
  account_id: string;
  account_name: string;
  platform?: string;
  can_view: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

interface EditUserModalProps {
  isOpen: boolean;
  user: TeamMember;
  onClose: () => void;
  onSuccess: () => void;
  onUpdate: (userId: string, data: UpdateUserRequest) => Promise<void>;
  availableAccounts?: SimpleAccountAccess[];
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  user,
  onClose,
  onSuccess,
  onUpdate,
  availableAccounts = []
}) => {
  // Initialize all hooks first (must be called in same order every render)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [role, setRole] = useState(user?.role || 'viewer');
  const [status, setStatus] = useState(user?.status || 'pending');
  const [accountAccess, setAccountAccess] = useState<SimpleAccountAccess[]>([]);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);

  // Reset form when user changes
  useEffect(() => {
    if (user) {
      setRole(user.role);
      setStatus(user.status);
      setAccountAccess((user.account_access || []).map(acc => ({
        account_id: acc.account_id,
        account_name: acc.account_name,
        platform: acc.platform,
        can_view: acc.permissions.can_view,
        can_edit: acc.permissions.can_edit,
        can_delete: acc.permissions.can_delete
      })));
      setError(null);
      setShowDeactivateConfirm(false);
    }
  }, [user]);

  // Early return AFTER all hooks are called
  if (!isOpen || !user) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await onUpdate(user.id, {
        role,
        status,
        account_access: accountAccess.map(acc => ({
          account_id: acc.account_id,
          can_view: acc.can_view,
          can_edit: acc.can_edit,
          can_delete: acc.can_delete
        }))
      });
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async () => {
    setLoading(true);
    setError(null);

    try {
      await onUpdate(user.id, { status: 'deactivated' });
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to deactivate user');
    } finally {
      setLoading(false);
      setShowDeactivateConfirm(false);
    }
  };

  const toggleAccountAccess = (accountId: string) => {
    setAccountAccess(prev => {
      const exists = prev.find(a => a.account_id === accountId);
      if (exists) {
        return prev.filter(a => a.account_id !== accountId);
      } else {
        const account = availableAccounts.find(a => a.account_id === accountId);
        if (account) {
          return [...prev, { ...account, can_view: true, can_edit: false, can_delete: false }];
        }
        return prev;
      }
    });
  };

  const updatePermission = (accountId: string, field: 'can_view' | 'can_edit' | 'can_delete', value: boolean) => {
    setAccountAccess(prev => prev.map(acc => 
      acc.account_id === accountId ? { ...acc, [field]: value } : acc
    ));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Edit User</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors text-2xl"
            disabled={loading}
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          {/* User Info (Read-only) */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <div className="text-gray-900">{user.email}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <div className="text-gray-900">{user.full_name}</div>
              </div>
            </div>
          </div>

          {/* Role */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as any)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              disabled={loading}
            >
              <option value="admin">Admin - Full system access</option>
              <option value="manager">Manager - Create/edit campaigns</option>
              <option value="analyst">Analyst - View and report</option>
              <option value="viewer">Viewer - View only</option>
            </select>
          </div>

          {/* Status */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status <span className="text-red-500">*</span>
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as any)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              disabled={loading}
            >
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="deactivated">Deactivated</option>
            </select>
            {status === 'deactivated' && (
              <p className="mt-2 text-sm text-amber-600">
                ⚠️ This user will lose access to the platform
              </p>
            )}
          </div>

          {/* Account Access */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Account Access
            </label>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              {availableAccounts.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  No ad accounts available
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {availableAccounts.map((account) => {
                    const hasAccess = accountAccess.find(a => a.account_id === account.account_id);
                    
                    return (
                      <div key={account.account_id} className="p-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                          <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!hasAccess}
                              onChange={() => toggleAccountAccess(account.account_id)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              disabled={loading}
                            />
                            <span className="text-sm font-medium text-gray-900">
                              {account.account_name}
                            </span>
                          </label>
                        </div>
                        
                        {hasAccess && (
                          <div className="ml-7 flex space-x-4">
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={hasAccess.can_view}
                                onChange={(e) => updatePermission(account.account_id, 'can_view', e.target.checked)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                disabled={loading}
                              />
                              <span className="text-sm text-gray-700">View</span>
                            </label>
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={hasAccess.can_edit}
                                onChange={(e) => updatePermission(account.account_id, 'can_edit', e.target.checked)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                disabled={loading}
                              />
                              <span className="text-sm text-gray-700">Edit</span>
                            </label>
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={hasAccess.can_delete}
                                onChange={(e) => updatePermission(account.account_id, 'can_delete', e.target.checked)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                disabled={loading}
                              />
                              <span className="text-sm text-gray-700">Delete</span>
                            </label>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <div>
              {status !== 'deactivated' && (
                <button
                  type="button"
                  onClick={() => setShowDeactivateConfirm(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-300"
                  disabled={loading}
                >
                  Deactivate User
                </button>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>

        {/* Deactivate Confirmation Modal */}
        {showDeactivateConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Confirm Deactivation
              </h3>
              <p className="text-gray-700 mb-6">
                Are you sure you want to deactivate <strong>{user.full_name}</strong>? 
                They will lose access to the platform immediately.
              </p>
              <div className="flex space-x-3 justify-end">
                <button
                  onClick={() => setShowDeactivateConfirm(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeactivate}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-300"
                  disabled={loading}
                >
                  {loading ? 'Deactivating...' : 'Yes, Deactivate'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
